import { Button, emitUnhandledError, logger, RichLiveField, TemplatePicker } from '@/ui'
import { format } from 'date-fns'
import { SendIcon, XIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { stripHtml } from './strip-html'
import type { ComposeMode, Email, EmailContact } from './types'

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
}

function getFirstName(fullName: string): string {
	const firstName = fullName.split(' ')[0]
	return firstName.length > 15 ? firstName.substring(0, 12) + '...' : firstName
}

export interface EmailComposerProps {
	mode: ComposeMode
	replyToEmail?: Email
	recruiter: EmailContact
	candidateContact: EmailContact
	onSend: (
		email: Omit<Email, 'id' | 'sentAt' | 'isRead' | 'isFromCandidate' | 'attachments' | 'readBy'>,
	) => Promise<void>
	onCancel: () => void
}

export function EmailComposer({
	mode,
	replyToEmail,
	recruiter,
	candidateContact,
	onSend,
	onCancel,
}: EmailComposerProps) {
	const { t } = useTranslation('common')
	const initialSubject = useMemo(() => {
		if (!replyToEmail) { return '' }
		if (mode === 'reply') {
			return replyToEmail.subject.startsWith('Re:')
				? replyToEmail.subject
				: `Re: ${replyToEmail.subject}`
		}
		return ''
	}, [mode, replyToEmail])

	const initialBody = useMemo(() => {
		if (mode === 'reply' && replyToEmail) {
			const plainBody = stripHtml(replyToEmail.body)
			const escapedBody = escapeHtml(plainBody)
			const escapedName = escapeHtml(replyToEmail.from.name)
			const quotedLines = escapedBody.split('\n').map(line => `&gt; ${line}`).join('<br>')
			return `<p><br></p><p>On ${
				format(replyToEmail.sentAt, 'PP')
			}, ${escapedName} wrote:</p><p>${quotedLines}</p>`
		}
		return ''
	}, [mode, replyToEmail])

	const [subject, setSubject] = useState(initialSubject)
	const [body, setBody] = useState(initialBody)
	const [isSending, setIsSending] = useState(false)

	useEffect(() => {
		setSubject(initialSubject)
		setBody(initialBody)
	}, [initialSubject, initialBody])

	const handleSend = useCallback(async () => {
		const bodyText = stripHtml(body).trim()
		if (!candidateContact.email || !bodyText) { return }

		setIsSending(true)
		try {
			await onSend({
				subject: subject.trim(),
				from: recruiter,
				to: candidateContact,
				body,
			})
		} catch (err) {
			logger.error(err, {
				tags: { component: 'EmailComposer', action: 'send' },
			})
			emitUnhandledError({
				title: t('emailComposer.errorTitle'),
				description: err instanceof Error ? err.message : t('emailComposer.errorDefault'),
				cause: err,
			})
		} finally {
			setIsSending(false)
		}
	}, [candidateContact, subject, body, onSend, recruiter, t])

	const canSend = candidateContact.email && stripHtml(body).trim()

	// Keyboard shortcut for Cmd+Enter to send
	const containerRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSend && !isSending) {
				e.preventDefault()
				void handleSend()
			}
		}

		const container = containerRef.current
		if (container) {
			container.addEventListener('keydown', handleKeyDown)
			return () => container.removeEventListener('keydown', handleKeyDown)
		}
	}, [canSend, isSending, handleSend])

	return (
		<motion.div
			ref={containerRef}
			initial={{ y: 20, opacity: 0 }}
			animate={{ y: 0, opacity: 1 }}
			exit={{ y: 20, opacity: 0 }}
			className="border-t border-gray-200 bg-white flex flex-col max-h-[50vh] shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.1)]"
		>
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
				<div className="flex items-center gap-2">
					<SendIcon className="size-4 text-brand-600" />
					<h3 className="text-sm font-medium text-gray-900">
						{mode === 'reply' ? t('emailComposer.reply') : t('emailComposer.newEmail')}
					</h3>
					<TemplatePicker
						onSelect={(template) => {
							if (template.subject) { setSubject(template.subject) }
							setBody(template.body)
						}}
					/>
				</div>
				<button
					type="button"
					onClick={onCancel}
					aria-label={t('common.dismiss')}
					className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
				>
					<XIcon className="size-4" aria-hidden="true" />
				</button>
			</div>

			{/* Form - scrollable */}
			<div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
				{/* To field */}
				<div className="flex items-baseline gap-3">
					<label className="text-sm font-medium text-gray-500 w-16 flex-shrink-0">
						{t('emailComposer.toLabel')}
					</label>
					<div className="ph-no-capture flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 flex items-center gap-2">
						<div className="size-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
							<span className="text-xs font-semibold text-brand-700">
								{candidateContact.name.charAt(0).toUpperCase()}
							</span>
						</div>
						<span>{candidateContact.name}</span>
						<span className="text-gray-400">&lt;{candidateContact.email}&gt;</span>
					</div>
				</div>

				{/* Subject field */}
				<div className="flex items-baseline gap-3">
					<label htmlFor="subject-input" className="text-sm font-medium text-gray-500 w-16 flex-shrink-0">
						{t('emailComposer.subjectLabel')}
					</label>
					<input
						id="subject-input"
						type="text"
						value={subject}
						onChange={(e) => setSubject(e.target.value)}
						placeholder={t('emailComposer.subjectPlaceholder')}
						className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
					/>
				</div>

				{/* Body */}
				<div className="flex flex-col border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:border-brand-500 transition-all min-h-[200px]">
					<RichLiveField
						key={`${mode}-${replyToEmail?.id ?? 'new'}`}
						value={body}
						onChange={setBody}
						outputFormat="html"
						disableMedia
						showStaticToolbar
					/>
				</div>
			</div>

			{/* Actions - fixed at bottom */}
			<div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30 flex-shrink-0">
				<div className="text-xs text-gray-400">
					Press{' '}
					<kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-[10px]">
						Cmd/Ctrl
					</kbd>
					+
					<kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-[10px]">Enter</kbd>
					{' '}
					to send
				</div>
				<div className="flex items-center gap-3">
					<Button type="subtle" size="short" onClick={onCancel}>
						{t('common.cancel')}
					</Button>
					<Button
						type="committal"
						size="short"
						onClick={handleSend}
						disabled={!canSend}
						isLoading={isSending}
					>
						<span className="flex items-center gap-2">
							<SendIcon className="size-4" />
							{t('emailComposer.sendTo', { name: getFirstName(candidateContact.name) })}
						</span>
					</Button>
				</div>
			</div>
		</motion.div>
	)
}
